import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CLOSE_DIALOGUE } from "../../store/dialogue/dialogue.type";
import {
  updateSourcingRequestStatus,
  sendQuote,
} from "../../store/sourcingRequest/sourcingRequest.action";
import dayjs from "dayjs";

const SourcingRequestDialog = () => {
  const dispatch = useDispatch();
  const { dialogueData } = useSelector((state) => state.dialogue);

  const [status, setStatus] = useState(dialogueData?.status || "Pending");
  const [adminNotes, setAdminNotes] = useState(dialogueData?.adminNotes || "");

  // Quote fields
  const [quotedPrice, setQuotedPrice] = useState(
    dialogueData?.quotedPrice || ""
  );
  const [quotedShippingCost, setQuotedShippingCost] = useState(
    dialogueData?.quotedShippingCost || ""
  );
  const [quoteMessage, setQuoteMessage] = useState(
    dialogueData?.quoteMessage || ""
  );

  const isQuoted = ["Quoted", "Accepted", "Paid"].includes(
    dialogueData?.status
  );
  const isPaid = dialogueData?.status === "Paid";

  const handleUpdate = () => {
    dispatch(
      updateSourcingRequestStatus(dialogueData?._id, { status, adminNotes })
    );
    dispatch({ type: CLOSE_DIALOGUE });
  };

  const handleSendQuote = () => {
    if (!quotedPrice || Number(quotedPrice) <= 0) {
      alert("Please enter a valid quoted price");
      return;
    }
    dispatch(
      sendQuote(dialogueData?._id, {
        quotedPrice: Number(quotedPrice),
        quotedShippingCost: Number(quotedShippingCost) || 0,
        quoteMessage,
      })
    );
    dispatch({ type: CLOSE_DIALOGUE });
  };

  const getStatusBadge = (s) => {
    const badges = {
      Pending: "bg-warning text-dark",
      "In Progress": "bg-info",
      Quoted: "bg-primary",
      Accepted: "bg-success",
      Paid: "bg-success",
      Rejected: "bg-danger",
    };
    return badges[s] || "bg-secondary";
  };

  return (
    <div className="mainDialogue fade-in">
      <div
        className="Dialogue"
        style={{ width: "750px", maxWidth: "95%", overflow: "auto", maxHeight: "90vh" }}
      >
        <div className="dialogueHeader">
          <div className="headerTitle fw-bold">Sourcing Request Details</div>
          <div
            className="closeBtn"
            onClick={() => dispatch({ type: CLOSE_DIALOGUE })}
          >
            <i className="fa-solid fa-xmark"></i>
          </div>
        </div>

        <div className="dialogueMain" style={{ padding: "20px" }}>
          {/* Customer Info */}
          <h6 className="text-dark fw-bold mb-3">Customer Information</h6>
          <div className="row mb-3">
            <div className="col-6">
              <label className="text-muted small">Full Name</label>
              <p className="text-dark mb-1">{dialogueData?.fullName}</p>
            </div>
            <div className="col-6">
              <label className="text-muted small">Email</label>
              <p className="text-dark mb-1">{dialogueData?.email}</p>
            </div>
            <div className="col-6">
              <label className="text-muted small">WhatsApp</label>
              <p className="text-dark mb-1">{dialogueData?.whatsappNumber}</p>
            </div>
            <div className="col-6">
              <label className="text-muted small">Address</label>
              <p className="text-dark mb-1">{dialogueData?.address}</p>
            </div>
          </div>

          <hr style={{ borderColor: "#E8E1D3" }} />

          {/* Product Info */}
          <h6 className="text-dark fw-bold mb-3">Product Details</h6>
          <div className="row mb-3">
            <div className="col-6">
              <label className="text-muted small">Product Name</label>
              <p className="text-dark mb-1">{dialogueData?.productName}</p>
            </div>
            <div className="col-6">
              <label className="text-muted small">Category</label>
              <p className="text-dark mb-1">
                {dialogueData?.category?.name || "-"}
              </p>
            </div>
            <div className="col-12">
              <label className="text-muted small">Description</label>
              <p className="text-dark mb-1">
                {dialogueData?.productDescription}
              </p>
            </div>
            <div className="col-4">
              <label className="text-muted small">Budget Range</label>
              <p className="text-dark mb-1">
                {dialogueData?.budgetMin} - {dialogueData?.budgetMax}
              </p>
            </div>
            <div className="col-4">
              <label className="text-muted small">Quantity</label>
              <p className="text-dark mb-1">{dialogueData?.quantity}</p>
            </div>
            <div className="col-4">
              <label className="text-muted small">Urgency</label>
              <p className="text-dark mb-1">{dialogueData?.urgency}</p>
            </div>
          </div>

          {/* Images */}
          {dialogueData?.images && dialogueData.images.length > 0 && (
            <>
              <hr style={{ borderColor: "#E8E1D3" }} />
              <h6 className="text-dark fw-bold mb-3">Reference Images</h6>
              <div className="d-flex gap-3 mb-3 flex-wrap">
                {dialogueData.images.map((img, i) => (
                  <a href={img} target="_blank" rel="noreferrer" key={i}>
                    <img
                      src={img}
                      alt={`ref-${i}`}
                      style={{
                        width: "120px",
                        height: "120px",
                        objectFit: "cover",
                        borderRadius: "8px",
                        border: "1px solid #E8E1D3",
                      }}
                    />
                  </a>
                ))}
              </div>
            </>
          )}

          <hr style={{ borderColor: "#E8E1D3" }} />

          {/* Meta */}
          <div className="row mb-3">
            <div className="col-4">
              <label className="text-muted small">Submitted</label>
              <p className="text-dark mb-1">
                {dayjs(dialogueData?.createdAt).format("DD/MM/YYYY HH:mm")}
              </p>
            </div>
            <div className="col-4">
              <label className="text-muted small">Linked User</label>
              <p className="text-dark mb-1">
                {dialogueData?.userId?.firstName}{" "}
                {dialogueData?.userId?.lastName || ""}
              </p>
            </div>
            <div className="col-4">
              <label className="text-muted small">Current Status</label>
              <p className="mb-1">
                <span
                  className={`badge ${getStatusBadge(dialogueData?.status)}`}
                >
                  {dialogueData?.status}
                </span>
              </p>
            </div>
          </div>

          {/* Payment info if paid */}
          {isPaid && (
            <>
              <hr style={{ borderColor: "#E8E1D3" }} />
              <h6 className="text-dark fw-bold mb-3">
                <i className="fa-solid fa-check-circle text-success me-2"></i>
                Payment Confirmed
              </h6>
              <div className="row mb-3">
                <div className="col-4">
                  <label className="text-muted small">Paid At</label>
                  <p className="text-dark mb-1">
                    {dialogueData?.paidAt
                      ? dayjs(dialogueData.paidAt).format("DD/MM/YYYY HH:mm")
                      : "-"}
                  </p>
                </div>
                <div className="col-4">
                  <label className="text-muted small">Gateway</label>
                  <p className="text-dark mb-1">
                    {dialogueData?.paymentGateway || "-"}
                  </p>
                </div>
                <div className="col-4">
                  <label className="text-muted small">Reference</label>
                  <p
                    className="text-dark mb-1"
                    style={{ fontSize: "11px", wordBreak: "break-all" }}
                  >
                    {dialogueData?.paymentReference || "-"}
                  </p>
                </div>
              </div>
            </>
          )}

          {/* Existing quote info if already quoted */}
          {isQuoted && (
            <>
              <hr style={{ borderColor: "#E8E1D3" }} />
              <h6 className="text-dark fw-bold mb-3">Quote Details</h6>
              <div
                className="p-3 mb-3"
                style={{
                  backgroundColor: "#eaf7ef",
                  borderRadius: "8px",
                  border: "1px solid #bfe3cd",
                }}
              >
                <div className="row">
                  <div className="col-4">
                    <label className="text-muted small">Quoted Price</label>
                    <p className="text-dark fw-bold mb-1">
                      ${dialogueData?.quotedPrice}
                    </p>
                  </div>
                  <div className="col-4">
                    <label className="text-muted small">Shipping Cost</label>
                    <p className="text-dark fw-bold mb-1">
                      ${dialogueData?.quotedShippingCost || 0}
                    </p>
                  </div>
                  <div className="col-4">
                    <label className="text-muted small">Total</label>
                    <p className="text-success fw-bold mb-1">
                      ${(dialogueData?.quotedPrice || 0) +
                        (dialogueData?.quotedShippingCost || 0)}
                    </p>
                  </div>
                </div>
                {dialogueData?.quoteMessage && (
                  <div className="mt-2">
                    <label className="text-muted small">Message to Customer</label>
                    <p className="text-dark mb-0">
                      {dialogueData.quoteMessage}
                    </p>
                  </div>
                )}
                {dialogueData?.quotedAt && (
                  <div className="mt-2">
                    <small className="text-muted">
                      Quoted on:{" "}
                      {dayjs(dialogueData.quotedAt).format("DD/MM/YYYY HH:mm")}
                    </small>
                  </div>
                )}
              </div>
            </>
          )}

          <hr style={{ borderColor: "#E8E1D3" }} />

          {/* Send Quote Section (only if not yet quoted/paid) */}
          {!isQuoted && (
            <>
              <h6 className="text-dark fw-bold mb-3">
                <i className="fa-solid fa-paper-plane me-2"></i>
                Send Price Quote
              </h6>
              <div
                className="p-3 mb-3"
                style={{
                  backgroundColor: "#eef4ff",
                  borderRadius: "8px",
                  border: "1px solid #cfe0fb",
                }}
              >
                <div className="row mb-3">
                  <div className="col-6">
                    <label className="text-muted small mb-1">
                      Quoted Price (USD) *
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      value={quotedPrice}
                      onChange={(e) => setQuotedPrice(e.target.value)}
                      placeholder="Enter item price..."
                      style={{
                        border: "1px solid #ccc",
                      }}
                    />
                  </div>
                  <div className="col-6">
                    <label className="text-muted small mb-1">
                      Shipping Cost (USD)
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      value={quotedShippingCost}
                      onChange={(e) => setQuotedShippingCost(e.target.value)}
                      placeholder="Enter shipping cost..."
                      style={{
                        border: "1px solid #ccc",
                      }}
                    />
                  </div>
                </div>
                {quotedPrice > 0 && (
                  <div className="mb-3">
                    <span className="text-muted small">Total: </span>
                    <span className="text-success fw-bold">
                      ${(Number(quotedPrice) || 0) +
                        (Number(quotedShippingCost) || 0)}
                    </span>
                  </div>
                )}
                <div className="mb-3">
                  <label className="text-muted small mb-1">
                    Message to Customer
                  </label>
                  <textarea
                    className="form-control"
                    rows="2"
                    value={quoteMessage}
                    onChange={(e) => setQuoteMessage(e.target.value)}
                    placeholder="e.g. We found your item! Here's the price breakdown..."
                    style={{
                      border: "1px solid #ccc",
                    }}
                  />
                </div>
                <button
                  className="btn btn-primary w-100"
                  onClick={handleSendQuote}
                  disabled={!quotedPrice || Number(quotedPrice) <= 0}
                >
                  <i className="fa-solid fa-paper-plane me-2"></i>
                  Send Quote & Notify Customer
                </button>
              </div>

              <hr style={{ borderColor: "#E8E1D3" }} />
            </>
          )}

          {/* Admin Status & Notes */}
          <h6 className="text-dark fw-bold mb-3">Update Status</h6>
          <div className="row mb-3">
            <div className="col-6">
              <label className="text-muted small mb-1">Status</label>
              <select
                className="form-control"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                style={{
                  border: "1px solid #ccc",
                }}
              >
                <option value="Pending">Pending</option>
                <option value="In Progress">In Progress</option>
                <option value="Quoted">Quoted</option>
                <option value="Accepted">Accepted</option>
                <option value="Paid">Paid</option>
                <option value="Rejected">Rejected</option>
              </select>
            </div>
          </div>
          <div className="mb-3">
            <label className="text-muted small mb-1">Admin Notes</label>
            <textarea
              className="form-control"
              rows="3"
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              placeholder="Add internal notes..."
              style={{
                border: "1px solid #ccc",
              }}
            />
          </div>

          <div className="d-flex justify-content-end gap-2">
            <button
              className="btn btn-secondary"
              onClick={() => dispatch({ type: CLOSE_DIALOGUE })}
            >
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleUpdate}>
              Update Request
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SourcingRequestDialog;
